import { useCallback, useMemo, useState } from 'react';

import {
  useFocusEffect,
  useNavigation,
} from '@react-navigation/native';
import { XStack, YStack, useMedia } from 'tamagui';
import { useShallow } from 'zustand/react/shallow';

import { ScreenWrapper } from '@south-street-app/molecules';
import { useChatStore } from '@south-street-app/stores';
import { refreshUnreadCounts } from '@south-street-app/utils';

import { ArchivedChatsModal } from './ArchivedChatsModal';
import { ChatInboxList } from './ChatInboxList';
import { useConversationPreview } from '../ChatScreen/useConversationPreview';
import { SupportConversationContent } from '../SupportChatScreen/SupportConversationContent';
import { UpdatesConversationContent } from '../UpdatesChatScreen/UpdatesConversationContent';

type DesktopSelection = { kind: 'support' } | { kind: 'updates' };

const ChatInboxScreen = () => {
  const { desktop } = useMedia();
  const navigation = useNavigation();
  const [desktopSelection, setDesktopSelection] = useState<DesktopSelection>({
    kind: 'support',
  });
  const [archivedModalVisible, setArchivedModalVisible] = useState(false);
  const {
    supportConversation,
    supportUnreadCount,
    updatesConversation,
    updatesUnreadCount,
    inboxRefreshKey,
  } = useChatStore(
    useShallow((state) => ({
      supportConversation: state.supportConversation,
      updatesConversation: state.updatesConversation,
      supportUnreadCount: state.supportUnreadCount,
      updatesUnreadCount: state.updatesUnreadCount,
      inboxRefreshKey: state.inboxRefreshKey,
    })),
  );
  const supportPreview = useConversationPreview(
    supportConversation,
    inboxRefreshKey,
  );
  const updatesPreview = useConversationPreview(
    updatesConversation,
    inboxRefreshKey,
  );

  useFocusEffect(
    useCallback(() => {
      useChatStore.getState().bumpInboxRefreshKey();

      refreshUnreadCounts().catch(() => undefined);
    }, []),
  );

  const activeDesktopConversation = useMemo(() => {
    if (desktopSelection.kind === 'updates') {
      return (
        <UpdatesConversationContent
          conversation={updatesConversation}
          inlineTitle={'Updates'}
        />
      );
    }

    return (
      <SupportConversationContent
        conversation={supportConversation}
        inlineTitle={'Chat'}
      />
    );
  }, [
    desktopSelection.kind,
    supportConversation,
    updatesConversation,
  ]);

  const handleOpenSupport = useCallback(() => {
    if (desktop) {
      setDesktopSelection({ kind: 'support' });

      return;
    }

    navigation.navigate('SupportChatScreen');
  }, [desktop, navigation]);

  const handleOpenUpdates = useCallback(() => {
    if (desktop) {
      setDesktopSelection({ kind: 'updates' });

      return;
    }

    navigation.navigate('UpdatesChatScreen');
  }, [desktop, navigation]);

  const handleOpenArchived = useCallback(() => {
    if (desktop) {
      setArchivedModalVisible(true);

      return;
    }

    navigation.navigate('ArchivedChatsScreen');
  }, [desktop, navigation]);

  const inboxRows = [
    {
      id: 'support' as const,
      kind: 'support' as const,
      title: 'Trading Desk',
      subtitle: supportPreview?.body ?? 'Message South Street support',
      timestamp:
        supportPreview?.dateCreated ??
        supportConversation?.lastMessage?.dateCreated,
      unreadCount: supportUnreadCount,
      selected: desktopSelection.kind === 'support',
    },
    {
      id: 'updates' as const,
      kind: 'updates' as const,
      title: 'Updates',
      subtitle: updatesPreview?.body ?? 'Broadcast updates from South Street',
      timestamp:
        updatesPreview?.dateCreated ??
        updatesConversation?.lastMessage?.dateCreated,
      unreadCount: updatesUnreadCount,
      selected: desktopSelection.kind === 'updates',
    },
  ];

  const inboxContent = (
    <ChatInboxList
      isDesktop={desktop}
      onPressArchived={handleOpenArchived}
      onPressRow={(kind) => {
        if (kind === 'updates') {
          handleOpenUpdates();

          return;
        }

        handleOpenSupport();
      }}
      rows={inboxRows}
    />
  );

  if (!desktop) {
    return <ScreenWrapper>{inboxContent}</ScreenWrapper>;
  }

  return (
    <ScreenWrapper>
      <YStack alignItems={'center'} backgroundColor={'$mono200'} flex={1}>
        <XStack
          borderLeftColor={'$mono300'}
          borderLeftWidth={1}
          borderRightColor={'$mono300'}
          borderRightWidth={1}
          flex={1}
          maxWidth={1280}
          width={'100%'}
        >
          {inboxContent}
          <YStack
            backgroundColor={'$mono200'}
            flex={1}
            minWidth={0}
            overflow={'hidden'}
          >
            {activeDesktopConversation}
          </YStack>
        </XStack>
      </YStack>

      <ArchivedChatsModal
        onClose={() => setArchivedModalVisible(false)}
        visible={archivedModalVisible}
      />
    </ScreenWrapper>
  );
};

export { ChatInboxScreen };
